import React from 'react';
import { PercentageIndicator } from '@paretogroup/tess';
import { formatDateValueToInput } from '@shared/components/inputs/date/range-date-picker-with-arrow-input.utils';
import { formatValuesForDisplayBasedOnType } from '@shared/utils/strings';
import { METRICS_TYPES } from '@shared/constants/MetricsTypeConstants';
import { formatValueToCell, getTotalColumn } from '@shared/tables';
import { NON_COMPARISON_METRICS, NON_COMPARISON_METRICS_COLUMNS } from './campaign-table.constants';
import {
    COLUMN_PROPS, getTotalComparison,
} from './campaign-table.utils';
import { ColumnTableComparisonCell } from './column-table-comparison-cell';


export const formatCampaignMetricsToTableColumns = (
    metrics,
    ranges,
) => metrics.map((metric) => {
    const { value, label, type } = metric;
    const columnProps = COLUMN_PROPS[value];
    const hasComparisonPeriod = !!ranges?.comparisonDate;

    if (!columnProps) {
        if (hasComparisonPeriod
                && NON_COMPARISON_METRICS_COLUMNS.indexOf(value) < 0
                && type !== METRICS_TYPES.string) {
            return formatComparisonMetricColumn(ranges, metric);
        }
        return {
            ...COLUMN_PROPS.default,
            Header: label,
            accessor: value,
            hasComparisonPeriod,
            metric,
        };
    }
    return {
        ...columnProps,
        accessor: value,
        hasComparisonPeriod,
        metric,
    };
});

export const formatComparisonMetricColumn = (ranges, metric) => {
    const { value, label } = metric;
    const accessors = getComparisonAccessors(value);
    return {
        Header: label,
        mainAccessor: accessors.mainAccessor,
        accessor: value,
        hasComparisonPeriod: true,
        columns: getComparisonColumns(metric, ranges),
    };
};

const getComparisonAccessors = metric => ({
    comparison: `comparison_${metric}`,
    mainAccessor: `main_${metric}`,
    diff: `diff_${metric}`,
    percentageDiff: `percentage_diff_${metric}`,
});

const renderCell = type => ({ value }) => {
    if (type === 'percent') return (formatValuesForDisplayBasedOnType(100 * value, type));

    return formatValuesForDisplayBasedOnType(value, type);
};

const defaultCellComparison = ({
    column, value,
}) => formatValueToCell(value, column);

const getTotalComparisonDiff = (column, rows, percentageDiff) => {
    const value = getTotalComparison(column, rows, percentageDiff) as number;

    return (
        <PercentageIndicator
            label={formatValuesForDisplayBasedOnType(Math.abs(value), 'percent')}
            isPositiveNumber={value > 0}
        />
    );
};

const getTotalDiff = (column, rows, diff) => {
    const value = getTotalComparison(column, rows, diff);

    return formatValueToCell(value, column);
};

const getComparisonColumns = (metric, ranges) => {
    const accessors = getComparisonAccessors(metric.value);
    return [{
        Cell: renderCell(metric.type),
        Header: formatDateValueToInput({ ...ranges.date, separator: '—' }),
        accessor: accessors.mainAccessor,
        metric,
        width: 145,
        Total: ({ column, rows }) => getTotalColumn(column, rows),
    }, {
        Cell: renderCell(metric.type),
        Header: formatDateValueToInput({ ...ranges.comparisonDate, separator: '—' }),
        accessor: accessors.comparison,
        metric,
        width: 145,
        Total: ({ column, rows }) => getTotalColumn(column, rows),
    }, {
        Cell: defaultCellComparison,
        Header: 'Diff.',
        accessor: accessors.diff,
        metric,
        Total: ({ column, rows }) => getTotalDiff(column, rows, accessors.diff),
    }, {
        Cell: ColumnTableComparisonCell,
        Header: 'Diff. (%)',
        accessor: accessors.percentageDiff,
        metric,
        Total: ({ column, rows }) => getTotalComparisonDiff(column, rows, accessors.percentageDiff),
    }];
};

const getComparisonValues = (metric, value, comparisonValue) => {
    const accessors = getComparisonAccessors(metric);
    const percentageDiff = ((value - comparisonValue) * 100) / comparisonValue;
    const diff = value - comparisonValue;
    return {
        [accessors.mainAccessor]: value,
        [accessors.comparison]: comparisonValue,
        [accessors.diff]: diff,
        [accessors.percentageDiff]: percentageDiff,
    };
};

export const insertComparisonColumns = (
    campaigns, comparisonCampaigns,
) => campaigns.map((campaign) => {
    const { campaign_id: campaignId } = campaign;
    const comparisonCampaign = comparisonCampaigns[campaignId];

    return Object.entries(campaign).reduce((acc, [metric, value]) => {
        if (NON_COMPARISON_METRICS.indexOf(metric) >= 0) {
            return {
                ...acc,
                [metric]: value,
            };
        }
        const comparisonValue = comparisonCampaign ? comparisonCampaign[metric] : 0;
        return {
            ...acc,
            ...getComparisonValues(metric, value, comparisonValue),
        };
    }, {});
});
